/**
 * Extension Capability Detection Service
 * 
 * Detects browser extension availability and negotiates file transfer capabilities
 * with peers to determine optimal transfer paths for massive files (4GB+).
 */

import { EventEmitter } from "events";

export type MemoryPressureState = 'normal' | 'high' | 'critical';

export interface PressureChangedEvent {
  previous: MemoryPressureState;
  current: MemoryPressureState;
  maxPressure: number;
  timestamp: number;
}

type ExtensionCapabilityEventMap = {
  'pressure-changed': [PressureChangedEvent];
};

export interface PeerCapabilities {
  extensionVersion?: string;
  maxFileSize: number;  // Maximum supported file size in bytes
  supportedFeatures: {
    nativeCheckpointing: boolean;
    adaptiveChunking: boolean;
    deduplication: boolean;
    backgroundProcessing: boolean;
    fileSystemAccess: boolean;
  };
  memoryLimits: {
    maxMemoryUsage: number;
    checkpointThreshold: number;
    cleanupRatio: number;
  };
}

export interface ExtensionDetectionResult {
  available: boolean;
  version?: string;
  capabilities?: PeerCapabilities;
  error?: string;
}

// Memory and file size thresholds - Updated for production use
export const TRANSFER_THRESHOLDS = {
  OPFS_LIMIT: 10 * 1024 * 1024 * 1024,     // 10GB - OPFS path limit (most browsers support much more)
  EXTENSION_TRIGGER: 100 * 1024 * 1024,    // 100MB - Extension path trigger (conservative for testing)
  WARNING_SIZE: 50 * 1024 * 1024,          // 50MB - Warn user about large files
  CHECKPOINT_SIZE: 25 * 1024 * 1024,       // 25MB - Checkpoint trigger
  CLEANUP_RATIO: 0.3,                      // 30% cleanup on checkpoint
  MAX_EXTENSION_SIZE: 1024 * 1024 * 1024,  // 1GB - Maximum extension support (testing limit)
  // Dynamic adjustment factors
  MEMORY_PRESSURE_REDUCTION: 0.5,          // Reduce limits by 50% under memory pressure
  HIGH_MEMORY_THRESHOLD: 0.7,              // 70% memory usage triggers pressure mode
  CRITICAL_MEMORY_THRESHOLD: 0.85          // 85% memory usage triggers aggressive limits
} as const;

/**
 * Service for detecting and managing browser extension capabilities
 */
export class ExtensionCapabilityService {
  private extensionDetected: boolean | null = null;
  private extensionCapabilities: PeerCapabilities | null = null;
  private peerCapabilities = new Map<string, PeerCapabilities>();
  // Threshold-event-driven logging — only log on pressure state transition
  private lastPressureState: MemoryPressureState = 'normal';

  // Event bus for pressure-state transitions. The peer-state-store
  // subscribes to drive the `throttled` modifier on every attached peer
  // (PR-2 of docs/connection-state-machine.md). Composing rather than
  // extending EventEmitter keeps the public surface intact.
  private events: EventEmitter<ExtensionCapabilityEventMap> = new EventEmitter();

  /**
   * Subscribe to pressure-state transitions. Returns an unsubscribe function.
   *
   * Fires only on actual transitions (e.g. normal→high, high→critical,
   * critical→normal). Repeated reads of the same state are coalesced inside
   * `getDynamicThresholds`.
   */
  onPressureChanged(
    listener: (event: PressureChangedEvent) => void,
  ): () => void {
    this.events.on('pressure-changed', listener as never);
    return () => {
      this.events.off('pressure-changed', listener as never);
    };
  }

  /**
   * Read the most recently observed pressure state — used by the store on
   * its initial peer-attach so the `throttled` modifier reflects current
   * conditions even if no transition has fired since service init.
   */
  getCurrentPressureState(): MemoryPressureState {
    return this.lastPressureState;
  }

  /**
   * Internal: transition the pressure state and notify subscribers.
   * Idempotent — repeated calls with the same `next` value are no-ops.
   * The console-log side effect is preserved at the call sites for
   * historical compatibility (debug vs info severity differs by direction).
   */
  private setPressureState(next: MemoryPressureState, maxPressure: number): void {
    if (this.lastPressureState === next) return;
    const previous = this.lastPressureState;
    this.lastPressureState = next;
    this.events.emit('pressure-changed', {
      previous,
      current: next,
      maxPressure,
      timestamp: Date.now(),
    });
  }
  
  /**
   * Detect if the browser extension is available and functional
   * 
   * PERFORMANCE CRITICAL: This method MUST return immediately if already detected
   * to prevent blocking WebRTC handshake timing during file metadata exchange.
   */
  async detectExtension(): Promise<ExtensionDetectionResult> {
    try {
      // CRITICAL: Return cached result immediately to prevent blocking critical paths
      if (this.extensionDetected !== null) {
        return {
          available: this.extensionDetected,
          capabilities: this.extensionCapabilities || undefined
        };
      }
      // Check for extension-specific APIs or global objects (with timing synchronization)
      const hasExtensionAPI = await this.checkExtensionAPIs();
      
      if (!hasExtensionAPI) {
        this.extensionDetected = false;
        return { available: false, error: 'Extension APIs not found' };
      }

      // Try to communicate with the extension
      const extensionResponse = await this.pingExtension();
      
      if (!extensionResponse.success) {
        this.extensionDetected = false;
        return { 
          available: false, 
          error: `Extension communication failed: ${extensionResponse.error}` 
        };
      }

      // Get extension capabilities
      const capabilities = await this.getExtensionCapabilities();
      
      this.extensionDetected = true;
      this.extensionCapabilities = capabilities;
      
      console.log('[ExtensionCapability] Extension detected successfully', {
        version: extensionResponse.version,
        maxFileSize: capabilities.maxFileSize,
        features: capabilities.supportedFeatures
      });

      return {
        available: true,
        version: extensionResponse.version,
        capabilities
      };
      
    } catch (error) {
      console.error('[ExtensionCapability] Extension detection failed:', error);
      this.extensionDetected = false;
      return { 
        available: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Check for extension-specific APIs with timing synchronization
   */
  private async checkExtensionAPIs(): Promise<boolean> {
    // Check for File System Access API (required for native file operations)
    const hasFileSystemAPI = 'showDirectoryPicker' in window;
    
    // Check for Web Workers support
    const hasWorkerSupport = typeof Worker !== 'undefined';

    // Wait for MobyNet extension injection with timeout
    const hasMobyNetExtension = await this.waitForExtensionInjection();
    
    // Fallback: Check for Chrome extension runtime (but verify it's actually our extension)
    const hasChromeExtensionRuntime = (
      typeof window !== 'undefined' &&
      'chrome' in window &&
      (window as any).chrome?.runtime?.id
    );

    console.log('[ExtensionCapability] API detection results', {
      hasFileSystemAPI,
      hasMobyNetExtension,
      hasChromeExtensionRuntime,
      hasWorkerSupport,
      chromeRuntimeId: (window as any).chrome?.runtime?.id,
      mobyNetExtensionType: typeof (window as any).mobyNetExtension
    });

    // Require File System API and Web Workers, plus either MobyNet extension or Chrome runtime
    return hasFileSystemAPI && hasWorkerSupport && (hasMobyNetExtension || hasChromeExtensionRuntime);
  }

  /**
   * Wait for MobyNet extension injection with event-based synchronization
   */
  private waitForExtensionInjection(): Promise<boolean> {
    return new Promise((resolve) => {
      // Check if already available
      if (typeof window !== 'undefined' && 
          'mobyNetExtension' in window &&
          typeof (window as any).mobyNetExtension === 'object') {
        console.log('[ExtensionCapability] Extension already detected');
        resolve(true);
        return;
      }

      // Listen for extension ready event
      const handleExtensionReady = () => {
        console.log('[ExtensionCapability] Extension ready event received');
        window.removeEventListener('mobynet-extension-ready', handleExtensionReady);
        
        // Verify injection
        const isInjected = (
          'mobyNetExtension' in window &&
          typeof (window as any).mobyNetExtension === 'object'
        );
        
        console.log('[ExtensionCapability] Extension injection verified:', isInjected);
        resolve(isInjected);
      };

      // Add event listener
      window.addEventListener('mobynet-extension-ready', handleExtensionReady);
      
      // Timeout fallback after 2 seconds
      setTimeout(() => {
        window.removeEventListener('mobynet-extension-ready', handleExtensionReady);
        console.log('[ExtensionCapability] Extension injection timeout, fallback to runtime check');
        resolve(false);
      }, 2000);
    });
  }

  /**
   * Ping the extension to verify communication
   */
  private async pingExtension(): Promise<{ success: boolean; version?: string; error?: string }> {
    try {
      // Try to communicate with extension via postMessage or direct API
      if ('mobyNetExtension' in window) {
        const extension = (window as any).mobyNetExtension;
        const response = await extension.ping();
        return { success: true, version: response.version };
      }

      // Fallback: try chrome extension API
      if ('chrome' in window && (window as any).chrome.runtime) {
        return new Promise((resolve) => {
          const timeout = setTimeout(() => {
            resolve({ success: false, error: 'Extension ping timeout' });
          }, 5000);

          try {
            (window as any).chrome.runtime.sendMessage(
              { type: 'PING' }, 
              (response: any) => {
                clearTimeout(timeout);
                if (response && response.success) {
                  resolve({ success: true, version: response.version });
                } else {
                  resolve({ success: false, error: 'Invalid ping response' });
                }
              }
            );
          } catch (error) {
            clearTimeout(timeout);
            resolve({ success: false, error: 'Chrome runtime error' });
          }
        });
      }

      return { success: false, error: 'No extension communication method found' };
      
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown ping error' 
      };
    }
  }

  /**
   * Compute dynamic memory limits based on current memory pressure and thresholds
   */
  private async computeDynamicMemoryLimits(): Promise<PeerCapabilities['memoryLimits']> {
    try {
      const thresholds = await this.getDynamicThresholds();
      const { memoryMonitor } = await import('../lib/memory-monitor');
      const usage = await memoryMonitor.getCurrentUsage();

      // Fallbacks if memory info not available
      const limitMB = usage?.limitMB ?? 2048; // Typical Chromium heap limit when unknown
      const isHigh = !!(usage?.isHighUsage || usage?.isCriticalUsage || usage?.opfsIsHighUsage || usage?.opfsIsCriticalUsage);

      // Use more conservative reservation under high pressure
      const reserveFraction = isHigh ? TRANSFER_THRESHOLDS.MEMORY_PRESSURE_REDUCTION : 0.7; // 50% if high pressure, else 70%

      const maxMemoryUsage = Math.floor(limitMB * reserveFraction) * 1024 * 1024; // bytes
      return {
        maxMemoryUsage,
        checkpointThreshold: thresholds.CHECKPOINT_SIZE,
        cleanupRatio: thresholds.CLEANUP_RATIO,
      };
    } catch (err) {
      console.warn('[ExtensionCapability] Failed to compute dynamic memory limits, using conservative defaults', err);
      return {
        maxMemoryUsage: 768 * 1024 * 1024, // 768MB conservative fallback
        checkpointThreshold: TRANSFER_THRESHOLDS.CHECKPOINT_SIZE,
        cleanupRatio: TRANSFER_THRESHOLDS.CLEANUP_RATIO,
      };
    }
  }

  /**
   * Public accessor for dynamic memory limits
   */
  async getDynamicMemoryLimits(): Promise<PeerCapabilities['memoryLimits']> {
    return this.computeDynamicMemoryLimits();
  }

  /**
   * Get detailed extension capabilities
   */
  private async getExtensionCapabilities(): Promise<PeerCapabilities> {
    // Default capabilities for detected extension (with dynamic memory limits)
    const dynamicMemoryLimits = await this.computeDynamicMemoryLimits();
    const defaultCapabilities: PeerCapabilities = {
      maxFileSize: TRANSFER_THRESHOLDS.MAX_EXTENSION_SIZE,
      supportedFeatures: {
        nativeCheckpointing: true,
        adaptiveChunking: true,
        deduplication: true,
        backgroundProcessing: true,
        fileSystemAccess: 'showDirectoryPicker' in window
      },
      memoryLimits: dynamicMemoryLimits
    };

    try {
      // Try to get capabilities from extension
      if ('mobyNetExtension' in window) {
        const extension = (window as any).mobyNetExtension;
        const capabilities = await extension.getCapabilities();
        return { ...defaultCapabilities, ...capabilities };
      }

      return defaultCapabilities;
      
    } catch (error) {
      console.warn('[ExtensionCapability] Could not get detailed capabilities, using defaults:', error);
      return defaultCapabilities;
    }
  }

  /**
   * Store peer capabilities received during file transfer metadata exchange
   * This replaces the stub logic that assumed all peers had identical capabilities
   */
  async storePeerCapabilities(peerId: string, capabilities: PeerCapabilities): Promise<void> {
    try {
      // Store the peer capabilities in our cache
      this.peerCapabilities.set(peerId, capabilities);
      
      console.log('[ExtensionCapability] Stored peer capabilities', {
        peerId: peerId.slice(0, 8) + '...',
        hasExtension: !!capabilities.extensionVersion,
        maxFileSize: (capabilities.maxFileSize / 1024 / 1024).toFixed(1) + 'MB',
        features: Object.keys(capabilities.supportedFeatures).filter(k => 
          capabilities.supportedFeatures[k as keyof typeof capabilities.supportedFeatures]
        )
      });
      
    } catch (error) {
      console.error('[ExtensionCapability] Failed to store peer capabilities:', error);
      throw error;
    }
  }

  /**
   * Negotiate capabilities with a peer using real peer-to-peer capability data
   * This method now uses stored capabilities from metadata exchange instead of stub assumptions
   */
  async negotiateCapabilities(peerId: string): Promise<PeerCapabilities> {
    try {
      // Check if we already have real capabilities for this peer from metadata exchange
      const storedCapabilities = this.peerCapabilities.get(peerId);
      if (storedCapabilities) {
        console.log('[ExtensionCapability] Using stored peer capabilities', {
          peerId: peerId.slice(0, 8) + '...',
          hasExtension: !!storedCapabilities.extensionVersion,
          maxFileSize: (storedCapabilities.maxFileSize / 1024 / 1024).toFixed(1) + 'MB'
        });
        return storedCapabilities;
      }

      // If no stored capabilities, return conservative OPFS-only assumptions
      // This handles backward compatibility with peers that don't support capability exchange
      const fallbackCapabilities: PeerCapabilities = {
        maxFileSize: TRANSFER_THRESHOLDS.OPFS_LIMIT,
        supportedFeatures: {
          nativeCheckpointing: false,
          adaptiveChunking: false,
          deduplication: false,
          backgroundProcessing: false,
          fileSystemAccess: false
        },
        memoryLimits: {
          maxMemoryUsage: 500 * 1024 * 1024, // 500MB
          checkpointThreshold: 100 * 1024 * 1024, // 100MB
          cleanupRatio: 0.5
        }
      };

      // Cache the fallback capabilities
      this.peerCapabilities.set(peerId, fallbackCapabilities);
      
      console.log('[ExtensionCapability] No stored capabilities found, using OPFS-only fallback for peer', {
        peerId: peerId.slice(0, 8) + '...',
        reason: 'legacy_peer_or_no_metadata_exchange'
      });

      return fallbackCapabilities;
      
    } catch (error) {
      console.error('[ExtensionCapability] Capability negotiation failed:', error);
      
      // Return minimal capabilities on error
      return {
        maxFileSize: TRANSFER_THRESHOLDS.OPFS_LIMIT,
        supportedFeatures: {
          nativeCheckpointing: false,
          adaptiveChunking: false,
          deduplication: false,
          backgroundProcessing: false,
          fileSystemAccess: false
        },
        memoryLimits: {
          maxMemoryUsage: 500 * 1024 * 1024, // 500MB
          checkpointThreshold: 100 * 1024 * 1024, // 100MB
          cleanupRatio: 0.5
        }
      };
    }
  }

  /**
   * Get our own capabilities
   */
  async getOurCapabilities(): Promise<PeerCapabilities> {
    const detection = await this.detectExtension();
    
    if (detection.available && detection.capabilities) {
      return detection.capabilities;
    }

    // Return OPFS-only capabilities
    const memoryLimits = await this.computeDynamicMemoryLimits();
    return {
      maxFileSize: TRANSFER_THRESHOLDS.OPFS_LIMIT,
      supportedFeatures: {
        nativeCheckpointing: false,
        adaptiveChunking: false,
        deduplication: false,
        backgroundProcessing: false,
        fileSystemAccess: false
      },
      memoryLimits
    };
  }

  /**
   * Get dynamic thresholds adjusted for current memory pressure
   */
  async getDynamicThresholds(): Promise<typeof TRANSFER_THRESHOLDS & { adjusted: boolean }> {
    try {
      // Import memory monitor dynamically to avoid circular dependency
      const { memoryMonitor } = await import('../lib/memory-monitor');
      const memoryUsage = await memoryMonitor.getCurrentUsage();
      
      if (!memoryUsage) {
        return { ...TRANSFER_THRESHOLDS, adjusted: false };
      }

      const jsMemoryPressure = memoryUsage.usagePercentage / 100;
      const opfsMemoryPressure = (memoryUsage.opfsUsagePercentage || 0) / 100;
      const maxPressure = Math.max(jsMemoryPressure, opfsMemoryPressure);

      // No adjustment needed if memory pressure is low
      if (maxPressure < TRANSFER_THRESHOLDS.HIGH_MEMORY_THRESHOLD) {
        if (this.lastPressureState !== 'normal') {
          console.debug('[ExtensionCapability] Memory pressure recovered to normal', {
            previous: this.lastPressureState,
            maxPressure: (maxPressure * 100).toFixed(1) + '%',
          });
          this.setPressureState('normal', maxPressure);
        }
        return { ...TRANSFER_THRESHOLDS, adjusted: false };
      }

      // Calculate reduction factor based on memory pressure
      let reductionFactor = 1.0;
      if (maxPressure >= TRANSFER_THRESHOLDS.CRITICAL_MEMORY_THRESHOLD) {
        // Critical memory pressure: aggressive reduction
        reductionFactor = TRANSFER_THRESHOLDS.MEMORY_PRESSURE_REDUCTION * 0.5; // 25% of original
      } else if (maxPressure >= TRANSFER_THRESHOLDS.HIGH_MEMORY_THRESHOLD) {
        // High memory pressure: moderate reduction
        reductionFactor = TRANSFER_THRESHOLDS.MEMORY_PRESSURE_REDUCTION; // 50% of original
      }

      const adjustedThresholds = {
        ...TRANSFER_THRESHOLDS,
        OPFS_LIMIT: Math.floor(TRANSFER_THRESHOLDS.OPFS_LIMIT * reductionFactor),
        EXTENSION_TRIGGER: Math.floor(TRANSFER_THRESHOLDS.EXTENSION_TRIGGER * reductionFactor),
        WARNING_SIZE: Math.floor(TRANSFER_THRESHOLDS.WARNING_SIZE * reductionFactor),
        CHECKPOINT_SIZE: Math.floor(TRANSFER_THRESHOLDS.CHECKPOINT_SIZE * reductionFactor),
        MAX_EXTENSION_SIZE: Math.floor(TRANSFER_THRESHOLDS.MAX_EXTENSION_SIZE * reductionFactor),
        adjusted: true
      };

      const newPressureState: MemoryPressureState = maxPressure >= TRANSFER_THRESHOLDS.CRITICAL_MEMORY_THRESHOLD ? 'critical' : 'high';
      if (newPressureState !== this.lastPressureState) {
        console.log(`[ExtensionCapability] Memory pressure entered ${newPressureState}`, {
          previous: this.lastPressureState,
          reductionFactor: (reductionFactor * 100).toFixed(1) + '%',
          adjustedOPFSLimit: this.formatFileSize(adjustedThresholds.OPFS_LIMIT),
          adjustedExtensionTrigger: this.formatFileSize(adjustedThresholds.EXTENSION_TRIGGER),
          maxPressure: (maxPressure * 100).toFixed(1) + '%',
        });
        this.setPressureState(newPressureState, maxPressure);
      }

      return adjustedThresholds;
    } catch (error) {
      console.warn('[ExtensionCapability] Failed to get dynamic thresholds, using defaults:', error);
      return { ...TRANSFER_THRESHOLDS, adjusted: false };
    }
  }

  /**
   * Check if large file transfer is supported for given file size (with dynamic adjustment)
   */
  async isLargeFileSupported(fileSize: number): Promise<boolean> {
    const thresholds = await this.getDynamicThresholds();
    
    if (fileSize <= thresholds.OPFS_LIMIT) {
      return true; // Always supported via OPFS
    }

    return this.extensionDetected === true && 
           this.extensionCapabilities !== null &&
           fileSize <= this.extensionCapabilities.maxFileSize;
  }

  /**
   * Get recommended transfer path for a file
   */
  async getRecommendedTransferPath(
    fileSize: number, 
    peerId?: string
  ): Promise<'opfs' | 'extension' | 'unsupported'> {
    
    // Small files always use OPFS (up to extension trigger threshold)
    if (fileSize <= TRANSFER_THRESHOLDS.EXTENSION_TRIGGER) {
      return 'opfs';
    }

    // Check our extension capability
    const detection = await this.detectExtension();
    if (!detection.available) {
      // Graceful fallback: use OPFS for large files when extension not available
      console.log('[ExtensionCapability] Extension not available, graceful fallback to OPFS for large file');
      return 'opfs';
    }

    // Determine effective extension max using detected capabilities and peer caps
    const thresholds = await this.getDynamicThresholds();
    let extensionMax = detection.capabilities?.maxFileSize ?? TRANSFER_THRESHOLDS.MAX_EXTENSION_SIZE;

    if (peerId) {
      const peerCapabilities = await this.negotiateCapabilities(peerId);
      // Use the most restrictive limit between us and the peer
      extensionMax = Math.min(extensionMax, peerCapabilities.maxFileSize);
    }

    // Prefer extension when within negotiated extension capacity
    if (fileSize <= extensionMax) {
      return 'extension';
    }

    // If the file exceeds extension capacity, gracefully fall back to OPFS when possible
    if (fileSize <= thresholds.OPFS_LIMIT) {
      console.log('[ExtensionCapability] File exceeds extension limit; falling back to OPFS within OPFS limit');
      return 'opfs';
    }

    return 'unsupported';
  }

  /**
   * Format file size for display
   */
  private formatFileSize(bytes: number): string {
    const gb = bytes / (1024 * 1024 * 1024);
    const mb = bytes / (1024 * 1024);
    
    if (gb >= 1) {
      return `${gb.toFixed(1)}GB`;
    } else if (mb >= 1) {
      return `${mb.toFixed(0)}MB`;
    } else {
      return `${(bytes / 1024).toFixed(0)}KB`;
    }
  }

  /**
   * Clear cached peer capabilities
   */
  clearPeerCapabilities(peerId?: string): void {
    if (peerId) {
      this.peerCapabilities.delete(peerId);
    } else {
      this.peerCapabilities.clear();
    }
  }

  /**
   * Reset extension detection (for testing)
   */
  resetDetection(): void {
    this.extensionDetected = null;
    this.extensionCapabilities = null;
    this.peerCapabilities.clear();
    // Reset pressure state silently (no event) — tests want a clean slate.
    this.lastPressureState = 'normal';
    this.events.removeAllListeners();
  }
}

// Singleton instance
export const extensionCapabilityService = new ExtensionCapabilityService();
